import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getCurrentUser } from '@/lib/auth';

interface AdminJobs {
  count: number;
  total: number;
  jobs: Array<{
    jobId: string;
    url: string;
    domain: string;
    composite: number;
    model: string;
    cost: number;
    inputTokens: number;
    outputTokens: number;
    durationMs: number;
    submittedAt: string;
  }>;
}

interface AdminAccounting {
  totals: { jobs: number; costUsd: number; inputTokens: number; outputTokens: number };
  byModel: Array<{ model: string; jobs: number; costUsd: number; inputTokens: number; outputTokens: number }>;
  byDay: Array<{ day: string; jobs: number; costUsd: number }>;
  byDomain: Array<{ domain: string; jobs: number; costUsd: number }>;
}

interface AdminQueue {
  depth: number;
  preview: Array<{ messageId: string; insertionTime: string; dequeueCount: number; bodyText: string }>;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function Admin() {
  const user = useQuery({ queryKey: ['me'], queryFn: getCurrentUser });
  const queue = useQuery<AdminQueue>({
    queryKey: ['admin-queue'],
    queryFn: async () => (await fetch('/api/ops/queue')).json(),
    refetchInterval: 5_000,
  });
  const accounting = useQuery<AdminAccounting>({
    queryKey: ['admin-accounting'],
    queryFn: async () => (await fetch('/api/ops/accounting')).json(),
    refetchInterval: 30_000,
  });
  const jobs = useQuery<AdminJobs>({
    queryKey: ['admin-jobs'],
    queryFn: async () => (await fetch('/api/ops/jobs?limit=50')).json(),
    refetchInterval: 30_000,
  });

  const totals = accounting.data?.totals;
  const byDay = accounting.data?.byDay ?? [];
  const byDomain = accounting.data?.byDomain ?? [];

  // Cost projection: 7-day rolling average × 30 days. Conservative — uses the
  // last 7 calendar days available (which includes today's partial day).
  const last7 = byDay.slice(-7);
  const last7Cost = last7.reduce((acc, d) => acc + d.costUsd, 0);
  const dailyAvg = last7.length > 0 ? last7Cost / Math.max(last7.length, 1) : 0;
  const monthlyProjection = dailyAvg * 30;

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Admin</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Queue, history, cost. Auto-refresh: queue 5s · accounting/jobs 30s.
          </p>
        </div>
        {user.data && (
          <div className="text-right text-xs text-[var(--color-muted-foreground)]">
            <div>signed in as <span className="font-mono text-[var(--color-foreground)]">{user.data.userDetails}</span></div>
            <div>via {user.data.identityProvider}</div>
            <a href="/logout" className="text-[var(--color-primary)] hover:underline">sign out</a>
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader>
            <CardDescription>Queue depth</CardDescription>
            <CardTitle className="font-mono text-3xl">{queue.data?.depth ?? '—'}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-[var(--color-muted-foreground)]">
            {queue.data?.preview && queue.data.preview.length > 0 ? (
              <ul className="space-y-1">
                {queue.data.preview.slice(0, 3).map((m) => (
                  <li key={m.messageId} className="font-mono truncate" title={m.bodyText}>
                    {m.bodyText.slice(0, 40)}…
                  </li>
                ))}
              </ul>
            ) : (
              <span>queue empty</span>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Total evaluations</CardDescription>
            <CardTitle className="font-mono text-3xl">{totals?.jobs ?? '—'}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-[var(--color-muted-foreground)]">
            <div>{totals?.inputTokens.toLocaleString() ?? '—'} input tok</div>
            <div>{totals?.outputTokens.toLocaleString() ?? '—'} output tok</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Cost to date</CardDescription>
            <CardTitle className="font-mono text-3xl">${totals?.costUsd.toFixed(2) ?? '—'}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-[var(--color-muted-foreground)]">
            cumulative LLM + container + storage
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Monthly projection</CardDescription>
            <CardTitle className="font-mono text-3xl">${monthlyProjection.toFixed(2)}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-[var(--color-muted-foreground)]">
            <div>${dailyAvg.toFixed(4)} / day · last {last7.length} days × 30</div>
          </CardContent>
        </Card>
      </div>

      {byDay.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Cost by day</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byDay}>
                <CartesianGrid stroke="oklch(0.30 0.02 250)" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fill: 'oklch(0.65 0.02 250)', fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fill: 'oklch(0.65 0.02 250)', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: 'oklch(0.20 0.02 250)', border: '1px solid oklch(0.30 0.02 250)', borderRadius: 6 }}
                />
                <Bar dataKey="costUsd" fill="oklch(0.72 0.15 195)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        {accounting.data?.byModel && accounting.data.byModel.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>By model</CardTitle>
              <CardDescription>Mix and cost across the deployed Foundry models.</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-[var(--color-muted-foreground)]">
                  <tr>
                    <th className="px-2 py-1">Model</th>
                    <th className="px-2 py-1">Jobs</th>
                    <th className="px-2 py-1">$ total</th>
                    <th className="px-2 py-1">$ / job</th>
                    <th className="px-2 py-1">Tok / job</th>
                  </tr>
                </thead>
                <tbody>
                  {accounting.data.byModel.map((r) => {
                    const perJobCost = r.jobs > 0 ? r.costUsd / r.jobs : 0;
                    const perJobTok = r.jobs > 0 ? Math.round((r.inputTokens + r.outputTokens) / r.jobs) : 0;
                    return (
                      <tr key={r.model} className="border-t border-[var(--color-border)]">
                        <td className="px-2 py-1 font-mono">{r.model}</td>
                        <td className="px-2 py-1 font-mono">{r.jobs}</td>
                        <td className="px-2 py-1 font-mono">${r.costUsd.toFixed(4)}</td>
                        <td className="px-2 py-1 font-mono">${perJobCost.toFixed(4)}</td>
                        <td className="px-2 py-1 font-mono">{perJobTok.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {byDomain.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>By domain</CardTitle>
              <CardDescription>Top {Math.min(byDomain.length, 20)} most-evaluated domains.</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-[var(--color-muted-foreground)]">
                  <tr>
                    <th className="px-2 py-1">Domain</th>
                    <th className="px-2 py-1">Jobs</th>
                    <th className="px-2 py-1">$ total</th>
                  </tr>
                </thead>
                <tbody>
                  {byDomain.map((r) => (
                    <tr key={r.domain} className="border-t border-[var(--color-border)]">
                      <td className="px-2 py-1">
                        <a href={`https://${r.domain}`} target="_blank" rel="noreferrer" className="hover:text-[var(--color-primary)]">{r.domain}</a>
                      </td>
                      <td className="px-2 py-1 font-mono">{r.jobs}</td>
                      <td className="px-2 py-1 font-mono">${r.costUsd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Recent jobs</CardTitle>
          <CardDescription>{jobs.data?.total ?? 0} total · showing latest {jobs.data?.count ?? 0}</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-[var(--color-muted-foreground)]">
              <tr>
                <th className="px-2 py-1">When</th>
                <th className="px-2 py-1">Domain</th>
                <th className="px-2 py-1">Score</th>
                <th className="px-2 py-1">Model</th>
                <th className="px-2 py-1">$</th>
                <th className="px-2 py-1">Duration</th>
                <th className="px-2 py-1">Job ID</th>
              </tr>
            </thead>
            <tbody>
              {(jobs.data?.jobs ?? []).map((j) => (
                <tr key={j.jobId} className="border-t border-[var(--color-border)]">
                  <td className="px-2 py-1 text-xs text-[var(--color-muted-foreground)]">{timeAgo(j.submittedAt)}</td>
                  <td className="px-2 py-1">
                    <a href={j.url} target="_blank" rel="noreferrer" className="hover:text-[var(--color-primary)]">{j.domain}</a>
                  </td>
                  <td className="px-2 py-1 font-mono">{j.composite}</td>
                  <td className="px-2 py-1"><Badge variant="outline" className="font-mono text-xs">{j.model}</Badge></td>
                  <td className="px-2 py-1 font-mono">${j.cost.toFixed(4)}</td>
                  <td className="px-2 py-1 font-mono text-xs">{(j.durationMs / 1000).toFixed(1)}s</td>
                  <td className="px-2 py-1 font-mono text-xs">
                    <a href={`/api/jobs/${j.jobId}/report.md`} className="text-[var(--color-primary)] hover:underline">{j.jobId.slice(0, 10)}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
